import { useState } from 'react'
import { useAppSelector } from '../../../../../store/hooks'
import { selectAccountId } from '../../../../../store/auth/authSlice'
import { type CheckpointRequest, useCreateCheckpointMutation, useGetCheckpointQuery, useUpdateCheckpointMutation } from '../../../../../store/sync/sync'
import { type RightCapitalSettingsFormValues } from './model'

const RIGHT_CAPITAL_CONTACT_SYNC_MAX_OBJECT_COUNT = 5000

export const useRightCapitalContactSync = () => {
  const accountId = useAppSelector(selectAccountId)
  const checkpointRequest = {
    accountId,
    level: 'user',
    connector: 'rightcapital',
    direction: 'inbound',
    object: 'contact_operation_hashed',
    scheduling: '*/30 * * * *',
    payload: {
      maxObjectCount: RIGHT_CAPITAL_CONTACT_SYNC_MAX_OBJECT_COUNT,
    },
  } as CheckpointRequest
  const { data: checkpoint, isFetching: isGetCheckpointFetching } = useGetCheckpointQuery(checkpointRequest)
  const [createCheckpoint, { isLoading: isCreateCheckpointLoading }] = useCreateCheckpointMutation()
  const [updateCheckpoint, { isLoading: isUpdateCheckpointLoading }] = useUpdateCheckpointMutation()
  const [isUpdating, setIsUpdating] = useState<boolean>(false)

  const save = async (data: RightCapitalSettingsFormValues) => {
    try {
      setIsUpdating(true)
      if (!checkpoint?.data) {
        if (data.inbound.contact.autoSync) {
          await createCheckpoint(checkpointRequest)
        }
      } else {
        await updateCheckpoint({ ...checkpointRequest, active: data.inbound.contact.autoSync })
      }
    } catch (e) {
      console.error(e)
    } finally {
      setIsUpdating(false)
    }
  }

  const saveAutoSync = async (active: boolean) => {
    try {
      if (!checkpoint?.data) {
        if (active) await createCheckpoint(checkpointRequest)
      } else {
        await updateCheckpoint({ ...checkpointRequest, active })
      }
    } catch (e) {
      console.error(e)
    }
  }

  return {
    autoSync: checkpoint?.data?.active ?? false,
    checkpoint,
    isGetCheckpointFetching,
    isFetching: !isUpdating && isGetCheckpointFetching,
    isSaving: isCreateCheckpointLoading || isUpdateCheckpointLoading,
    save,
    saveAutoSync,
  }
}
